import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { UploadIcon } from "../Icons";
import { CustomTextarea } from "@components/Forms";
import { CHARACTER_LENGTH } from "@constants/index";
import CloneRepository from "./CloneRepository";

// Constants
const SCAN_TYPES = [
  { value: "B", label: "Binary" },
  { value: "S", label: "Source code" },
];

// File validation constants - COMMENTED OUT
// const ALLOWED_FILE_TYPES = [
//   "application/x-msdownload",
//   "application/zip",
//   "application/zip-compressed",
//   "application/x-zip-compressed",
//   "application/json",
// ];

// const ALLOWED_FILE_EXTENSIONS = [
//   "exe",
//   "zip",
//   "json",
//   "tar",
//   "gz",
//   "rar",
//   "7z",
//   "jar",
//   "war",
//   "ear",
//   "apk",
//   "deb",
//   "rpm",
//   "msi",
//   "dmg",
//   "pkg",
// ];


const ClarityVerificationRequest = ({ isOpen, onClose, onSubmit, setHasErrors, setRequestData, setRepoInfo }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      verifFile: null,
      importMethod: "upload",
      repositoryType: "PTA",
      url: "",
      accessToken: "",
      sshUrl: "",
      requestComment: "",
      verificationType: "clarity",
    },
    mode: "onChange",
  });

  const watchImportMethod = watch("importMethod");
  const watchRepositoryType = watch("repositoryType");
  const watchUrl = watch("url");
  const watchAccessToken = watch("accessToken");
  const watchSshUrl = watch("sshUrl");

  // File validation function - COMMENTED OUT
  // const validateFile = useCallback((file) => {
  //   if (!file) return false;

  //   const fileExtension = file.name.split(".").pop()?.toLowerCase();
  //   const isValidType = ALLOWED_FILE_TYPES.includes(file.type);
  //   const isValidExtension = ALLOWED_FILE_EXTENSIONS.includes(fileExtension);

  //   return isValidType && isValidExtension;
  // }, []);

  // Handle file selection
  const handleFileSelect = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        // Original validation logic - COMMENTED OUT
        // if (validateFile(file)) {
        //   setSelectedFile(file);
        //   setValue("verifFile", file);
        //   clearErrors("verifFile");
        // } else {
        //   setError("verifFile", {
        //     type: "manual",
        //     message: t("project.verification-request.file-validation-error"),
        //   });
        //   setSelectedFile(null);
        //   setValue("verifFile", null);
        // }

        // Simplified logic without validation
        setSelectedFile(file);
        setValue("verifFile", file);
        clearErrors("verifFile");
      }
    },
    // [setValue, setError, clearErrors, validateFile, t],
    [setValue, clearErrors, setError, t]
  );

  // Handle browse file button
  const handleBrowseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle form validation and data updates
  useEffect(() => {
    const subscription = watch((data) => {
      const trimmedData = Object.keys(data).reduce((acc, key) => {
        acc[key] = typeof data[key] === "string" ? data[key].trim() : data[key];
        return acc;
      }, {});

      let hasValidationErrors = false;

      // Validate based on import method
      if (trimmedData.importMethod === "upload") {
        hasValidationErrors = !selectedFile;
      } else if (trimmedData.importMethod === "repository") {
        if (trimmedData.repositoryType === "PTA") {
          hasValidationErrors = !trimmedData.url || !trimmedData.accessToken;
        } else if (trimmedData.repositoryType === "SSH") {
          hasValidationErrors = !trimmedData.sshUrl;
        }
      }

      setHasErrors(hasValidationErrors);

      // Ensure verifFile is always consistent - create a FileList-like object
      const verifFileData = selectedFile ? { 0: selectedFile, length: 1 } : null;
      setRequestData({ ...trimmedData, verifFile: verifFileData });
    });

    return () => subscription.unsubscribe();
  }, [watch, selectedFile, setHasErrors, setRequestData]);

  // Handle repository info updates
  useEffect(() => {
    if (watchImportMethod === "repository") {
      if (watchRepositoryType === "PTA" && watchUrl && watchAccessToken) {
        setRepoInfo({
          url: watchUrl.trim(),
          token: watchAccessToken.trim(),
        });
      } else if (watchRepositoryType === "SSH" && watchSshUrl) {
        setRepoInfo({
          url: watchSshUrl.trim(),
          token: undefined,
        });
      } else {
        setRepoInfo({});
      }
    } else {
      setRepoInfo({});
    }
  }, [watchImportMethod, watchRepositoryType, watchUrl, watchAccessToken, watchSshUrl, setRepoInfo]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    reset();
    setSelectedFile(null);
    setRepoInfo({});
    onClose();
  }, [reset, setRepoInfo, onClose]);

  // Handle form submission
  const onSubmitForm = useCallback(
    (data) => {
      // Ensure verifFile is always consistent - create a FileList-like object
      const verifFileData = selectedFile ? { 0: selectedFile, length: 1 } : null;

      const submitData = {
        ...data,
        verifFile: verifFileData,
        verificationType: "clarity", // Add verification type
      };

      if (onSubmit) {
        onSubmit(submitData);
      }
      handleCancel();
    },
    [selectedFile, onSubmit, handleCancel],
  );

  if (!isOpen) return null;

  return (
    <div
      data-testid="clarity-verification-request"
      className={`fixed inset-0 z-50 flex items-center justify-start md:justify-center ${colors.bgBlurModal} backdrop-blur-sm overflow-x-auto p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div className={`${colors.bgModal} max-h-[90vh] w-[672px] min-w-[672px] overflow-hidden rounded-lg shadow-xl`}>
        {/* Header */}
        <div className={`border-b px-6 py-4 ${colors.borderPrimary}`}>
          <h2 id="modal-title" className={`${colors.textPrimary} text-xl font-bold`}>
            {t("project.verification-request.clarity-title")}
          </h2>
          <p id="modal-description" className={`${colors.textSecondary} mt-1 text-sm`}>
            {t("project.verification-request.description")}
          </p>
        </div>

        {/* Content */}
        <form
          onSubmit={handleSubmit(onSubmitForm)}
          data-testid="clarity-verification-request-form"
          className={`max-h-[calc(90vh-8rem)] space-y-6 overflow-y-auto px-6 py-6`}
          noValidate
        >
          {/* Import Method */}
          <fieldset>
            <legend className={`${colors.textPrimary} mb-3 text-sm font-medium`}>{t("project.verification-request.import-method")}</legend>

            {/* Repository Option */}
            <CloneRepository
              control={control}
              colors={colors}
              t={t}
              watchImportMethod={watchImportMethod}
              watchRepositoryType={watchRepositoryType}
              watchSshUrl={watchSshUrl}
              errors={errors}
              urlRules={{
                required: watchImportMethod === "repository" && watchRepositoryType === "PTA" ? t("project.verification-request.url-required") : false,
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: t("project.verification-request.url-invalid"),
                },
              }}
              accessTokenRules={{
                required: watchImportMethod === "repository" && watchRepositoryType === "PTA" ? t("project.verification-request.token-required") : false,
                minLength: {
                  value: 10,
                  message: t("project.verification-request.token-min-length"),
                },
              }}
            />
            {/* Upload File Option */}
            <div
              className={`rounded-lg border p-4 transition-all duration-200 ${watchImportMethod === "upload" ? `${colors.borderSelectedOption} ${colors.bgSelectedOption}` : `${colors.borderPrimary}`
                }`}
            >
              <Controller
                name="importMethod"
                control={control}
                render={({ field }) => (
                  <label className="flex cursor-pointer items-center">
                    <input
                      type="radio"
                      {...field}
                      value="upload"
                      checked={field.value === "upload"}
                      className="sr-only"
                    />
                    <div
                      className={`mr-3 flex h-4 w-4 items-center justify-center rounded-full border-2 transition-colors ${field.value === "upload"
                        ? `${colors.borderSelectedOption} ${colors.bgSelectedRadio}`
                        : `${colors.borderPrimary} ${colors.bgPrimary}`
                        }`}
                      aria-hidden="true"
                    >
                      {field.value === "upload" && <div className={`h-2 w-2 rounded-full ${colors.toggleThumbBackground}`}></div>}
                    </div>
                    <div className="flex flex-1 items-center justify-between">
                      <span className={`${colors.textPrimary} text-sm font-medium`}>{t("project.verification-request.upload-file")}</span>
                      {field.value === "upload" && (
                        <div className="flex items-center space-x-2">
                          {selectedFile && (
                            <span className={`${colors.textSecondary} text-sm max-w-[350px] truncate`}
                              title={selectedFile.name}
                            >{selectedFile.name}</span>
                          )}
                          <button
                            type="button"
                            data-testid="clarity-verification-request-browse-file-button"
                            onClick={handleBrowseFile}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium ${colors.textActive} transition-colors ${colors.textHoverActive}`}
                            aria-label={selectedFile ? t("project.verification-request.change-file") : t("project.verification-request.browse-file")}
                          >
                            <UploadIcon size={20} className={colors.textActive} />
                            <span>{selectedFile ? t("project.verification-request.change-file") : t("project.verification-request.browse-file")}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </label>
                )}
              />
              {/* File validation error display - COMMENTED OUT */}
              {/* {errors.verifFile && <p className="mt-2 text-xs text-red-500">{errors.verifFile.message}</p>} */}
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              // accept=".zip,.tar,.gz,.rar,.7z,.jar,.war,.ear,.apk,.deb,.rpm,.msi,.exe,.dmg,.pkg,.json"
              aria-hidden="true"
            />
          </fieldset>

          {/* Request Message */}
          <div>
            <label htmlFor="requestComment" className={`${colors.textPrimary} mb-3 block text-sm font-medium`}>
              {t("project.verification-request.request-comment")}
            </label>
            <Controller
              name="requestComment"
              control={control}
              render={({ field }) => (
                <>
                  <CustomTextarea
                    {...field}
                    id="requestComment"
                    data-testid="clarity-verification-request-message-input"
                    placeholder={t("project.verification-request.write-comment")}
                    rows={4}
                    maxLength={CHARACTER_LENGTH.REQUEST}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="clarity-verification-request-message-char-count">
                    {(field.value || "").length}/{CHARACTER_LENGTH.REQUEST}
                  </p>
                </>
              )}
            />
          </div>
        </form>

        {/* Footer */}
        <div className={`flex items-center justify-end space-x-3 border-t px-6 py-4 ${colors.borderPrimary}`}>
          <button
            type="button"
            data-testid="clarity-verification-request-cancel-button"
            onClick={handleCancel}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${colors.borderOutLineButton} ${colors.textActive} ${colors.bgPrimary} ${colors.hover}`}
          >
            {t("project.verification-request.cancel")}
          </button>
          <button
            type="button"
            data-testid="clarity-verification-request-submit-button"
            onClick={handleSubmit(onSubmitForm)}
            disabled={
              !isValid ||
              (watchImportMethod === "upload" && !selectedFile) ||
              (watchImportMethod === "repository" &&
                ((watchRepositoryType === "PTA" && (!watchUrl || !watchAccessToken)) ||
                  (watchRepositoryType === "SSH" && !watchSshUrl)))
            }
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${!isValid ||
              (watchImportMethod === "upload" && !selectedFile) ||
              (watchImportMethod === "repository" &&
                ((watchRepositoryType === "PTA" && (!watchUrl || !watchAccessToken)) ||
                  (watchRepositoryType === "SSH" && !watchSshUrl)))
              ? `border ${colors.borderDisabled} ${colors.textDisabledControl} cursor-not-allowed opacity-50`
              : colors.buttonVerificationRequest
              }`}
          >
            {t("project.verification-request.request")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClarityVerificationRequest;